import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const notifications = {
    quickCapture: {
        saved_one: 'Capture added to Pending',
        saved_other: '{{count}} captures added to Pending',
        failedTitle: 'Could not read a capture',
        failedBody: 'Try again from Shortcuts or enter the amount manually.',
    },
    // app/notifications/banks.tsx — gerenciar bancos monitorados
    banks: {
        title: 'Banks',
        searchPlaceholder: 'Search banks…',
        activeLabel: 'Active · {{total}}',
        availableLabel: 'Available · {{total}}',
        activate: 'Activate',
        empty: 'No banks found',
        deactivateA11y: 'Deactivate {{bank}}',
    },

    // app/notifications/index.tsx — caixa de pendentes + onboarding/bloqueio da captura
    inbox: {
        title: 'Notifications',
        iosTitle: 'Pending',
        dismissAllTitle: 'Dismiss all',
        dismissAllMessage: 'Dismiss all pending notifications?',
        cancel: 'Cancel',
        dismiss: 'Dismiss',

        // Selos de estado dos cartões de permissão
        badgeActive: 'Active',
        badgeBlocked: 'Blocked',
        badgeChecking: 'Checking',
        badgePending: 'Pending',
        recoveryPath: 'Recovery path',

        // Cartão do listener (leitura das notificações bancárias)
        listenerTitle: 'Bank notification reading',
        listenerActiveDesc: 'The Android listener is on and can capture purchases, Pix, TED and boletos.',
        listenerBlockedDesc: 'Android blocked access. Without it, new pending items won\'t come in automatically.',
        listenerPendingDesc: 'The listener isn\'t enabled yet. Turn it on to capture activity from supported banks.',
        listenerActionBlocked: 'Open notification access',
        listenerAction: 'Enable bank reading',
        listenerRecoveryPath: 'Android > Apps > Special access > Notification access > Kontto',

        // Cartão dos alertas locais (lembretes)
        reminderTitle: 'Local alerts and reminders',
        reminderActiveDesc: 'Local alerts and reminders can reappear on your device as soon as something new arrives.',
        reminderBlockedDesc: 'The app keeps reading notifications, but you lose local alerts and reminders until you allow it on Android.',
        reminderPendingDesc: 'Turn it on to get alerted when a pending item is captured and to sync local reminders.',
        reminderActionBlocked: 'Open app settings',
        reminderAction: 'Enable local alerts',
        reminderRecoveryPath: 'Android > Apps > Kontto > Notifications',

        // Aviso de captura muda (batimento do listener; ver lib/notificationListenerHealth)
        healthSilentTitle: 'Reading stopped receiving notifications',
        healthSilentDesc: 'Access is granted, but nothing has reached the app in over a day. Android usually froze the connection — reconnect below.',
        healthRestrictedDesc: 'Android is blocking Kontto in the background (restricted battery). Allow battery usage and reconnect reading.',
        healthBatteryRecoveryPath: 'Android > Apps > Kontto > Battery > Unrestricted',
        healthActionRebind: 'Reconnect reading',
        healthActionBattery: 'Open app settings',
        healthRebindTitle: 'Reconnection requested',
        healthRebindMessage: 'We asked Android to re-enable reading. Try it out: make a transaction that triggers a notification (a Pix, for example) and check if it shows up here.',
        healthRebindUnavailableTitle: 'Reconnection unavailable in this version',
        healthRebindUnavailableMessage: 'Turn notification access off and on in Android settings, or restart your device.',
        healthStuckTitle: 'Reading is stuck on this device',
        healthStuckDesc: 'Notifications are reaching Android, but not Kontto. Restarting the app fixes it — it takes just a blink.',
        healthActionRestart: 'Restart the app',
        healthRestartUnavailableTitle: 'Restart unavailable in this version',
        healthRestartUnavailableMessage: 'Force stop it manually: Android Settings > Apps > Kontto > Force stop. Then open Kontto again.',
        probeButton: 'Test reading now',
        probeRunning: 'Testing reading…',
        probeOkTitle: 'Reading is working',
        probeOkMessage: 'The test notification made it through end to end. Everything is alive.',
        probeOkRestrictedTitle: 'Works only while the app is open',
        probeOkRestrictedMessage: 'The test notification made it through, but Android is blocking Kontto in the background — with the app closed, nothing is captured. Allow it in: Settings > Apps > Kontto > Battery > Unrestricted.',
        probeDeadTitle: 'Reading did not respond',
        probeDeadMessage: 'The test notification never reached the app. Tap Reconnect reading; if that does not help, use Restart the app (or force stop it in Settings).',
        probeUnavailableTitle: 'Test unavailable',
        probeUnavailableMessage: 'To run the test, Kontto needs permission to send notifications (that is what creates the test notification). Enable it in Settings > Apps > Kontto > Notifications.',
        healthAutostartTitle: 'On your device, also turn on Autostart',
        healthAutostartPath: 'Settings > Apps > Manage apps > Kontto > Autostart',
        accessUnavailableTitle: "Couldn't open that screen",
        accessUnavailableMessage: 'Open it manually: Android Settings > Apps > Special access > Notification access > Kontto.',

        // Resumo de captura no topo da lista
        captureActiveTitle: 'Capture active',
        capturePartialTitle: 'Partial capture',
        captureActiveSubtitle: 'Reading and alerts working',
        capturePartialSubtitle: 'Reading active · turn on local alerts',

        banksMonitored: 'Monitored banks',
        activeOfTotal_one: '{{count}} active of {{total}} available',
        activeOfTotal_other: '{{count}} active of {{total}} available',
        manage: 'Manage',
        valesTitle: 'Vouchers and benefits',
        valeAvailableHint: '{{total}} available · tap to activate',
        smsTitle: 'Bank SMS',
        smsOnHint: 'Reading purchase SMS from monitored banks',
        smsOffHint: 'Off · tap to activate',
        pendingHeader: 'Pending · {{total}}',
        iosCaptureTitle: 'Quick capture on iPhone',
        iosCaptureSubtitle: 'Apple Pay and Double Back Tap entries arrive here for review.',
        iosCaptureSubtitleBank: 'Bank notifications, Apple Pay and Double Back Tap entries arrive here for review.',
        iosCaptureManage: 'Set up',

        // Onboarding de primeiro acesso
        heroTitle: 'Log everything automatically',
        heroSubtitle: 'Kontto reads notifications from your banking apps and detects purchases, Pix, TED and boletos automatically — you just confirm.',
        step1Title: 'Notification access',
        step1Desc: 'To capture activity',
        step2Title: 'Alerts and reminders',
        step2Desc: 'Alerts you when something new arrives',
        privacyNote: 'Everything stays on your device. Nothing is sent to external servers.',
        banksSupported: '{{total}} supported banks',
        wallMore: '+{{total}}',

        // Tela de acesso bloqueado
        blockedTitle: 'Access blocked',
        blockedSubtitle: 'Android revoked notification access. Without it, new activity won\'t come in on its own. Re-enable it in a few steps.',
        openAndroidSettings: 'Open Android settings',
        howToReactivate: 'How to re-enable',
        reactivateStep1: 'Open Android Settings',
        reactivateStep2: 'Apps › Special access › Notification access',
        reactivateStep3: 'Enable Kontto in the list',
        blockedRecoveryPath: 'Android › Apps › Special access › Notification access › Kontto',
        pausedNote: 'While blocked, automatic capture is paused',

        // Estado vazio da lista de pendentes
        emptyTitle: 'No pending transactions',
        emptySubtitle: 'When your banking apps send notifications about purchases, Pix, TED or boletos, they\'ll appear here to confirm.',
        iosEmptySubtitle: 'Use automatic Apple Pay or Double Back Tap to log an expense without opening Kontto.',
    },

    // lib/captureProbe.ts — the probe's silent test notification
    probe: {
        channelName: 'Notification reading test',
        title: 'Kontto',
        body: 'Checking notification reading…',
    },

    // app/notifications/vouchers.tsx — gerenciar vales/benefícios (recurso Pro)
    vouchers: {
        title: 'Vouchers and benefits',
        intro: 'Since you\'re Pro, Kontto already reads purchase notifications from your benefit cards on its own and creates transactions for you to confirm. Turn off whatever you don\'t want — your choice sticks.',
        introFree: 'With Pro, Kontto reads purchase notifications from your benefit cards on its own and creates transactions for you to confirm. Subscribing turns them all on at once.',
        reading: 'Reading purchases',
        notReading: 'Card purchases',
        activateA11y: 'Activate {{vale}}',
        deactivateA11y: 'Deactivate {{vale}}',
    },

    // app/notifications/sms.tsx — canal de SMS bancário (ADR 0048)
    sms: {
        title: 'Bank SMS',
        intro: 'Some banks still announce purchases via SMS. Kontto reads the messaging app\'s notification — never your SMS inbox — and only from known bank senders. Everything else is discarded immediately.',
        toggleTitle: 'Read purchase SMS',
        reading: 'Reading SMS from monitored banks',
        notReading: 'Off',
        activateA11y: 'Enable bank SMS reading',
        deactivateA11y: 'Disable bank SMS reading',
        privacyNote: 'Personal messages are never read or stored. Only bank SMS become transaction suggestions — and you confirm each one.',
        filtersNote: 'The banks you enabled and the ignored words in "Monitored banks" also apply to SMS.',
        banksSupported: '{{total}} banks with mapped SMS',
    },

    // app/notification-primer.tsx — primer de auto-detecção (Android)
    primer: {
        onboardingPill: 'Setup complete!',
        activationPill: 'First expense saved!',
        title: 'Let Kontto write it down',
        subtitle: 'When your bank flags a purchase, the transaction shows up here ready — all it needs is your ok.',
        exampleNotifHeader: 'Nubank · now',
        // Os trechos entre asteriscos saem em negrito (ver renderHighlighted no primer).
        exampleNotifText: 'Purchase approved for *R$ 32,90* at *PADARIA DOCE PÃO*',
        exampleMerchant: 'Padaria Doce Pão',
        // 'Food' = nome da categoria do seed EN (F4). Exemplo ilustrativo, não dado real.
        exampleSuggestionMeta: 'Expense · Food · to confirm',
        exampleAmount: 'R$ 32,90',
        guaranteePrivacy: 'Nothing leaves your phone — Kontto doesn\'t send your data anywhere.',
        guaranteeConfirm: 'You confirm every transaction before saving.',
        activate: 'Turn it on now',
        activateHint: 'Just one switch · turn it off whenever',
        dismiss: 'I\'d rather log by hand',
        grantedTitle: 'Done, reading is on',
        grantedDesc: 'On your next purchase Kontto will suggest the transaction right here.',
        retryTitle: 'Just one tap missing',
        retryDesc: 'The Kontto switch is still off in Settings. It\'s quick:',
        retryStep1Title: 'Tap “Try again”',
        retryStep1Desc: 'We\'ll open the Android settings for you.',
        retryStep2Title: 'Turn on the Kontto switch',
        retryStep3Title: 'Come back to Kontto',
        retryStep3Desc: 'We check here whether it worked.',
        retryPathPrefix: 'If you can\'t find it:',
        retryDismiss: 'Leave it for later',
        done: 'Done',
        retry: 'Try again',
    },

    // app/ios-bank-capture-primer.tsx — reading bank notifications on iPhone (iOS 27+, ADR 0152).
    iosPrimer: {
        newPill: 'New in iOS 27',
        title: 'Your iPhone now logs it for you',
        subtitle: 'When your bank sends a Pix or purchase alert, the entry shows up here ready to go. You just review it.',
        guaranteePrivacy: 'The notification is read on your iPhone. Nothing goes to any server.',
        step1Title: 'Add the Kontto shortcut',
        step1Desc: 'Tap the button below and confirm with “Add Shortcut”.',
        step2Title: 'Turn on the automation',
        step2Desc: 'In the Shortcuts app, go to Library › Automation and turn on “When I get a notification from any of… apps”. It arrives turned off.',
        step3Title: 'Done',
        step3Desc: 'Your next bank notification becomes an entry for you to review.',
        add: 'Add to Shortcuts',
        addHint: 'Nubank, Itaú, Inter, C6 and 40+ banks · 1 minute',
        confirm: 'I turned it on',
        openShortcuts: 'Open the Shortcuts app',
        dismiss: 'Not now',
        doneTitle: 'All set, reading is on',
        doneDesc: 'The next bank notification shows up in Pending. You can change it later in Settings → Quick capture.',
        openFailed: "Couldn't open Shortcuts. Please try again.",
    },

    // components/notifications/BankDetailSheet.tsx — detalhe do banco (tipos + palavras)
    bankDetail: {
        typesActive: '{{enabled}} of {{total}} types active',
        disabled: 'Bank disabled',
        activateA11y: 'Activate {{bank}}',
        typesSection: 'Transaction types',
        excludedSection: 'Ignored words',
        addWordPlaceholder: 'Add word…',
        excludedHint: 'Notifications with these words will be ignored',
    },

    // components/notifications/ConfirmTransactionSheet.tsx — confirmar lançamento
    confirm: {
        accountErrorReceived: 'Select the account that received it.',
        accountErrorSent: 'Select the account that sent it.',
        accountErrorDefault: 'Select the account.',
        accountLabel: 'Account',
        accountLabelReceive: 'Received in',
        accountLabelPay: 'Paid with',
        accountPlaceholder: 'Select account...',
        settleReceive: 'Yes, settle it',
        settleBill: 'Yes, settle it',
        confirmIncome: 'Confirm income',
        confirmExpense: 'Confirm expense',
        confirm: 'Confirm',
        dismiss: 'Dismiss',
        dismissItem: 'Dismiss {{name}}',
        descriptionPlaceholder: 'Add description',
        cardLabel: 'Card',
        cardPlaceholder: 'Select card...',
        installmentsLabel: 'Installments',
        installmentsValue: '{{n}}× of {{amount}}',
        installmentsCash: 'Pay in full',
        installmentDecrease: 'One fewer installment',
        installmentIncrease: 'One more installment',
        installmentCountA11y: 'Number of installments',
        transferSourceLabel: 'Source account',
        transferDestinationLabel: 'Destination account',
        categoryLabel: 'Category',
        categoryPlaceholder: 'Select category...',
        suggestedFromHistory: 'Suggested from history',
        suggestedFromContext: 'Suggested from day and time',
        suggestedFromSimilar: 'Suggested from similar transactions',
        suggestedFromMarketplace: 'You usually log {{name}} like this',
        suggestedFromCatalog: "The app's suggestion for this store",
        contextAlternative: 'Or maybe {{name}}',
        useContextAlternative: 'Use',
        // Remembered name and notes (ADR 0198).
        rememberedName: 'Name you used last time',
        useOriginalName: 'Use the original',
        notesLabel: 'Notes',
        notesPlaceholder: 'Notes',
        clearNotes: 'Clear notes',
        change: 'Change',
        addTags: 'Add tags',
        typeLabel: 'Type',
        typePlaceholder: 'Choose the type',
        typeReadHint: 'read from bank',
        typeReadReceiptHint: 'read from receipt',
        typeChangedHint: 'changed',
        typeSheetTitle: 'What was this transaction?',
        typeSheetSubtitle: '{{bank}} read it as {{type}} · {{amount}}',
        typeSheetSubtitleUnknown: '{{bank}} didn\'t say the type · {{amount}}',
        typeSheetSubtitleReceipt: 'On the receipt: {{type}} · {{amount}}',
        typeSheetSubtitleReceiptUnknown: 'The receipt doesn\'t say the type · {{amount}}',
        kindExpense: 'Money out',
        kindExpenseHint: 'Spending, a payment or a Pix to someone',
        kindDebit: 'Debit card purchase',
        kindDebitHint: 'Comes out of the account balance, not the bill',
        kindIncome: 'Money in',
        kindIncomeHint: 'Payment received, salary, refund',
        kindCard: 'Credit card purchase',
        kindCardHint: 'Goes to the bill, can be split',
        kindTransfer: 'Transfer between accounts',
        kindTransferHint: 'Not spending or income — it just moved',
        kindReserva: 'Save to pocket',
        kindReservaHint: 'Money set aside, not an expense',
        kindSettle: 'Scheduled bill payment',
        kindSettleIncome: 'Scheduled income',
        kindSettleCard: 'Charge already on the bill',
        kindSettleFinancing: 'Financing installment',
        kindSettleLoan: 'Receivable payment',
        kindSettleHint: 'Settles {{name}}',
        transfer: 'Transfer',
        reservaChip: 'Pocket',
        reservaLabel: 'Pocket',
        reservaPlaceholder: 'Select pocket...',
        reservaSheetTitle: 'Select pocket',
        confirmReserva: 'Save',
        confirmReservaTransfer: 'Transfer and save',
        reservaSourceLabel: 'From account',
        reservaCrossNote: 'Creates the transfer to {{dest}} and saves to the pocket.',
        reservaPartialTitle: 'Partially saved',
        reservaPartialBody: '{{amount}} fit in the account\'s Available balance — the rest was left out.',
        amountInvalid: 'Enter an amount greater than zero.',
        original: 'Original: {{amount}}',
        fixedRate: 'Fixed rate: 1 {{source}} = {{rate}}',
        directionExpense: 'Expense',
        directionIncome: 'Income',
        matchIncomeTitle: 'Is this the scheduled income?',
        matchBillTitle: 'Is this the payment for this scheduled bill?',
        matchCardTitle: 'Is this the charge already on the bill?',
        matchFinancingTitle: 'Is this the {{name}} installment?',
        matchLoanTitle: 'Is this {{name}} paying you back?',
        matchMetaForecast: 'projected for {{date}}',
        matchMetaDue: 'due {{date}}',
        matchMetaDuePast: 'was due {{date}}',
        matchMetaPurchase: 'posted on {{date}}',
        matchDateToday: 'today, {{date}}',
        matchDateTomorrow: 'tomorrow, {{date}}',
        matchAmountDiffAbove: '+ {{difference}}',
        matchAmountDiffBelow: '− {{difference}}',
        notThisBill: 'Not this bill',
        notThisIncome: 'Not this income',
        notThisCard: 'Not this charge',
        notThisFinancing: 'Not this installment',
        notThisLoan: 'Not this payment',
        stillScheduledBill: '{{name}} stays scheduled',
        stillScheduledIncome: '{{name}} stays expected',
        stillScheduledCard: '{{name}} stays on the bill',
        stillScheduledFinancing: 'The {{name}} installment stays open',
        stillScheduledLoan: '{{name}}\'s balance stays open',
        linkMatch: 'Link',
        invoiceHintTitle: 'Looks like a card bill payment',
        invoiceHintBody: 'Confirming here creates a regular expense and the bill stays open, so the amount would count twice. Pay it from the card screen.',
        invoiceHintAction: 'Go to the bill',
        settleHelper: 'The scheduled transaction will be completed with this notification\'s date and account, without creating a new one.',
        settleCardHelper: 'The charge already on the invoice is confirmed with this notification\'s date — nothing new is added to the invoice.',
        settleFinancingHelper: 'The installment is marked as paid with this notification\'s date and account, without creating a new transaction.',
        settleLoanHelper: 'The amount is applied to {{name}}\'s outstanding balance instead of becoming standalone income.',
        settleRecurringHelper: 'This occurrence of the recurring transaction is completed with this notification\'s date and account. The series keeps repeating as usual.',
        settlePaidOnCardHelper: 'Paid by card: the scheduled transaction leaves {{account}} and goes to the {{card}} invoice, without creating a new one.',
        settleRecurringPaidOnCardHelper: 'Paid by card this time: only this occurrence goes to the {{card}} invoice. The next ones stay on {{account}}.',
        settleAmountNote: 'It keeps this notification\'s amount, {{amount}}.',
        settleRecurringAmountNote: 'This one keeps {{amount}}; the next ones stay expected at {{expected}}.',
        settleRecurringGone: 'This recurring transaction is no longer active. Close and confirm again to create a new transaction.',
        settleCard: 'Yes, confirm charge',
        settleFinancing: 'Yes, pay installment',
        settleLoan: 'Yes, record payment',
        cardLabelSettle: 'Card invoice',
        cardLabelPaidOnCard: 'Paid by card',
        errorTitle: 'Couldn\'t confirm',
        errorFallback: 'Try again.',
    },

    // components/notifications/PendingNotificationsSection.tsx — bloco na Home
    pending: {
        sectionTitle: 'Pending ({{total}})',
    },
} satisfies DeepPartialStrings<TranslationSchema['notifications']>;
